import { ReactNode, useCallback, useEffect, useMemo } from "react";
import { useI18n, t } from "@bookingcom/lingojs-react";
import { pricingUtils } from "@flights/web-api-utils-universal";
import { UIUnifiedPriceBreakdown, UIUnifiedPriceBreakdownItemScope, UIUnifiedPriceItem } from "@flights/types";
import { useTrackUnifiedBreakdownPb } from "utils/experiments/pricing/flights_unified_price_breakdown_pb";
import useRouteName from "hooks/useRouteName";
import useProfiler from "hooks/useProfiler";

// group FLIGHT_ADULT, FLIGHT_CHILD, FLIGHT_INFANT into FLIGHT
export type BreakdownScopeId =
  | Exclude<UIUnifiedPriceBreakdownItemScope, "FLIGHT_ADULT" | "FLIGHT_CHILD" | "FLIGHT_INFANT">
  | "FLIGHT";

export type BreakdownScope = {
  id: BreakdownScopeId;
  name: string;
  items: UIUnifiedPriceItem[];
  expandedByDefault?: boolean;
};

export type BreakdownScopeItems = UIUnifiedPriceItem[];

export type BreakdownControllerProps = UIUnifiedPriceBreakdown & {
  expandedByDefault?: Partial<Record<UIUnifiedPriceBreakdownItemScope, boolean>>;
  isSummary?: boolean;
  footerSections?: Partial<Record<BreakdownScopeId, ReactNode | string>>;
  isHungaryDepartingRyanAir: boolean;
  isWithDiscount: boolean; // flights_web_unified_price_breakdown
};

export function useBreakdownController(props: BreakdownControllerProps) {
  const {
    price,
    items,
    addedItems,
    footerSections,
    isHungaryDepartingRyanAir,
    isWithDiscount,
    isSummary,
    expandedByDefault
  } = props;
  const i18n = useI18n();
  const { isSolo, travellersCount } = useProfiler();
  const routeName = useRouteName();

  const getSectionTitle = useCallback(
    (id?: BreakdownScopeId) => {
      switch (id) {
        case "FEE":
          return i18n.trans(t("flights_price_details_fees_title"));
        case "EXTRA":
          return i18n.trans(t("flights_price_details_heading_extras"));
        case "DISCOUNT":
          return i18n.trans(t("flights_price_details_heading_discounts"));
        default: {
          const travellerCount = travellersCount();

          return isSolo()
            ? i18n.trans(t("flights_price_details_heading_flight_solo"))
            : i18n.trans(
                t("flights_price_details_heading_flight_multi", {
                  variables: { num_travellers: travellerCount },
                  num_exception: travellerCount
                })
              );
        }
      }
    },
    [i18n, isSolo, travellersCount]
  );

  const { flightAdultItems, flightChildItems, flightInfantItems, extraItems, discountItems, feeItems } = useMemo(() => {
    const feeItems: BreakdownScopeItems = items.filter((item) => item.scope === "FEE") || [];
    const extraItems: BreakdownScopeItems = items.filter((item) => item.scope === "EXTRA") || [];
    const discountItems: BreakdownScopeItems = items.filter((item) => item.scope === "DISCOUNT") || [];
    const flightAdultItems: BreakdownScopeItems = items.filter((item) => item.scope === "FLIGHT_ADULT") || [];
    const flightChildItems: BreakdownScopeItems = items.filter((item) => item.scope === "FLIGHT_CHILD") || [];
    const flightInfantItems: BreakdownScopeItems = items.filter((item) => item.scope === "FLIGHT_INFANT") || [];
    return { flightAdultItems, flightChildItems, flightInfantItems, extraItems, discountItems, feeItems };
  }, [items]);

  const { extraAddedItems, discountAddedItems, feeAddedItems } = useMemo(() => {
    const feeAddedItems: BreakdownScopeItems = addedItems.filter((item) => item.scope === "FEE") || [];
    const extraAddedItems: BreakdownScopeItems = addedItems.filter((item) => item.scope === "EXTRA") || [];
    const discountAddedItems: BreakdownScopeItems = addedItems.filter((item) => item.scope === "DISCOUNT") || [];
    return { extraAddedItems, discountAddedItems, feeAddedItems };
  }, [addedItems]);

  const isExpandedByDefault = useCallback(
    (section: UIUnifiedPriceBreakdownItemScope | "FLIGHT") => {
      switch (section) {
        case "FEE":
        case "EXTRA":
        case "DISCOUNT":
        case "FLIGHT_ADULT":
        case "FLIGHT_CHILD":
        case "FLIGHT_INFANT":
          return !!expandedByDefault?.[section];
        case "FLIGHT":
          return (
            !!expandedByDefault?.FLIGHT_ADULT || !!expandedByDefault?.FLIGHT_CHILD || !!expandedByDefault?.FLIGHT_INFANT
          );
        default:
          return false;
      }
    },
    [expandedByDefault]
  );

  const sections = useMemo(() => {
    const allSections: Partial<Record<BreakdownScopeId, BreakdownScope>> = {};
    const fee = [...feeItems, ...feeAddedItems];
    const extra = [...extraItems, ...extraAddedItems];
    const discount = [...discountItems, ...discountAddedItems];
    const flight = [...flightAdultItems, ...flightChildItems, ...flightInfantItems];
    if (fee.length)
      allSections.FEE = {
        id: "FEE",
        name: getSectionTitle("FEE"),
        items: fee,
        expandedByDefault: isExpandedByDefault("FEE")
      };
    if (extra.length)
      allSections.EXTRA = {
        id: "EXTRA",
        name: getSectionTitle("EXTRA"),
        items: extra,
        expandedByDefault: isExpandedByDefault("EXTRA")
      };
    if (flight.length)
      allSections.FLIGHT = {
        id: "FLIGHT",
        name: getSectionTitle(),
        items: flight,
        expandedByDefault: isExpandedByDefault("FLIGHT")
      };
    if (discount.length)
      allSections.DISCOUNT = {
        id: "DISCOUNT",
        name: getSectionTitle("DISCOUNT"),
        items: discount,
        expandedByDefault: isExpandedByDefault("DISCOUNT")
      };
    return allSections;
  }, [
    feeItems,
    extraItems,
    feeAddedItems,
    discountItems,
    extraAddedItems,
    flightAdultItems,
    flightChildItems,
    flightInfantItems,
    discountAddedItems,
    isExpandedByDefault,
    getSectionTitle
  ]);

  const totalPriceTitle = useMemo(() => i18n.trans(t("flights_price_details_total_title")), [i18n]);

  const totalPriceFooter = useMemo(
    () =>
      isHungaryDepartingRyanAir
        ? i18n.trans(t("flights_ryanair_hungary_sr_price_single"))
        : i18n.trans(t("flights_price_details_includes_taxes")),
    [isHungaryDepartingRyanAir, i18n]
  );

  const totalPrice = useMemo(() => {
    const items = [...extraAddedItems, ...discountAddedItems, ...feeAddedItems].map((item) => item.price);
    return items.reduce((prev, current) => pricingUtils.sumPrice(prev, current), price);
  }, [price, feeAddedItems, extraAddedItems, discountAddedItems]);

  const isPostBooking = useMemo(
    () =>
      ["pb-order-details", "pb-add-baggage", "pb-add-fast-track", "pb-checkout-payment", "pb-seatmap"].includes(
        routeName
      ),
    [routeName]
  );

  // flights_unified_price_breakdown_pb -- start
  // ==========================================================================
  const { trackUnifiedPriceBreakdownPbGoals, trackUnifiedPriceBreakdownPbStages } = useTrackUnifiedBreakdownPb();
  useEffect(() => {
    if (!isPostBooking) return;

    trackUnifiedPriceBreakdownPbStages({ withDiscount: isWithDiscount });
  }, [trackUnifiedPriceBreakdownPbStages, isPostBooking, i18n, sections.DISCOUNT, isWithDiscount]);
  useEffect(() => {
    if (!isPostBooking) return;

    const ancillariesBreakdownExists = sections.EXTRA?.items.some((item) => item.items.length > 0);
    if (ancillariesBreakdownExists) {
      trackUnifiedPriceBreakdownPbGoals.ancillariesBreakdownExists();
    }
  }, [trackUnifiedPriceBreakdownPbGoals, sections.EXTRA, isPostBooking]);
  // ==========================================================================
  // flights_unified_price_breakdown_pb -- end

  const onExpand = useCallback(
    (scope: string) => {
      return (id: string) => {
        // flights_unified_price_breakdown_pb -- start
        // ==========================================================================
        if (isPostBooking) {
          if (scope === "FLIGHT") {
            if (id === "flight_adult") trackUnifiedPriceBreakdownPbGoals.accordionAdult();
            if (id === "flight_child") trackUnifiedPriceBreakdownPbGoals.accordionNonAdult();
            if (id === "flight_infant") trackUnifiedPriceBreakdownPbGoals.accordionNonAdult();
          }
          if (scope === "EXTRA") {
            trackUnifiedPriceBreakdownPbGoals.accordionAncillaries();
          }
        }
        // ==========================================================================
        // flights_unified_price_breakdown_pb -- end
      };
    },
    [trackUnifiedPriceBreakdownPbGoals, isPostBooking]
  );

  return {
    sections,
    totalPrice,
    footerSections,
    totalPriceTitle,
    totalPriceFooter,
    isSummary,
    onExpand // flights_unified_price_breakdown_pb
  };
}
